// What an operator just sent to the agents - a review comment, or a change to a next
// action - confirmed for a moment with the way to its conversation. Kept apart from the
// provider so the provider file exports only components (react-refresh/only-export-components).
import { createContext, useContext } from "react";


export interface Sent {
  what: "comment" | "change";
  /** "started", "queued" behind the turn answering an earlier one, "unsent" when no
   * review could be started (the change still stands), or "saved" when it was left for
   * the agent's next daily run on purpose. */
  status: "started" | "queued" | "unsent" | "saved";
  href: string | null;
}

export const SentNoticeContext = createContext<((sent: Sent) => void) | null>(
  null,
);

/** Shows the notice for something just sent. */
export const useSentNotice = (): ((sent: Sent) => void) => {
  const show = useContext(SentNoticeContext);
  if (!show) throw new Error("useSentNotice outside SentNoticeProvider");
  return show;
};

/** A change saved for the agent's next daily run rather than sent to it now: the page
 * shows it at once, and no agent is woken (Noaa, 29 Oct 2026). */
export const changeSaved = (): Sent => ({ what: "change", status: "saved", href: null });

export const sentText = ({ what, status }: Sent): string => {
  if (status === "saved") return "Change saved. The agent takes it in on its next daily run.";
  if (status === "unsent")
    return "Change saved. The agent could not be reached just now; it takes the change in on its next run.";
  const noun = what === "comment" ? "Comment" : "Change";
  return status === "queued"
    ? `${noun} queued - the agent takes it once it has finished with your earlier comments.`
    : `${noun} sent - the agent is working on it.`;
};
